import React, { useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import { LogOut, ShieldCheck, Brain, BarChart3, LayoutGrid, KeyRound, Menu, X, History, TrendingUp, GitCompare, Target, Factory, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ChangePasswordModal from "./ChangePasswordModal";

const COLLAPSE_KEY = "ameman_sidebar_collapsed";

export default function Layout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      return !c;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const navItems = [
    { to: "/", label: "Dashboard", icon: BarChart3 },
    { to: "/aplicatii", label: "Aplicatii", icon: LayoutGrid },
    { to: "/ai-board", label: "AI Board", icon: Brain },
    { to: "/istoric", label: "Istoric", icon: History },
    { to: "/evolutii", label: "Evolutii", icon: TrendingUp },
    { to: "/comparatii", label: "Comparatii", icon: GitCompare },
    { to: "/rata-succes", label: "Rata succes", icon: Target },
    { to: "/productie", label: "Productie", icon: Factory },
  ];

  const SidebarContent = ({ mini = false }) => (
    <div className="flex h-full flex-col">
      <div className={`flex items-center gap-3 border-b border-slate-200 py-5 ${mini ? "justify-center px-2" : "px-5"}`}>
        <div className="h-8 w-8 rounded bg-accent-500 text-ink-950 grid place-items-center font-bold">A</div>
        {!mini && (
          <div className="flex-1">
            <div className="text-xs tracking-wide text-slate-500">AmeMan</div>
            <div className="text-sm font-semibold leading-tight">Vedere de ansamblu</div>
          </div>
        )}
      </div>

      <nav className={`flex flex-col gap-1 py-4 flex-1 ${mini ? "px-2" : "px-3"}`}>
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            title={mini ? label : undefined}
            onClick={() => setMobileOpen(false)}
            data-testid={`nav-${to.replace("/", "") || "home"}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-2.5 rounded py-2 text-sm transition ${mini ? "justify-center px-2" : "px-3"} ${
                isActive ? "bg-accent-500 text-ink-950 font-medium" : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <Icon size={16} /> {!mini && label}
          </NavLink>
        ))}
      </nav>

      {user ? (
        <div className={`border-t border-slate-200 py-4 flex flex-col gap-2 ${mini ? "px-2" : "px-3"}`}>
          {!mini && (
            <div className="flex items-center gap-2 px-3 py-1 text-sm text-slate-600">
              <ShieldCheck size={16} className="text-accent-600 shrink-0" />
              <span data-testid="layout-user-info" className="truncate">{user.name} ({user.role})</span>
            </div>
          )}
          <button
            data-testid="layout-change-password-btn"
            type="button"
            title={mini ? "Schimba parola" : undefined}
            onClick={() => { setPwOpen(true); setMobileOpen(false); }}
            className={`inline-flex items-center gap-2 rounded border border-slate-300 py-2 text-sm hover:bg-slate-100 ${mini ? "justify-center px-2" : "px-3"}`}
          >
            <KeyRound size={14} /> {!mini && "Schimba parola"}
          </button>
          <button
            data-testid="layout-logout-btn"
            type="button"
            title={mini ? "Iesire" : undefined}
            onClick={handleLogout}
            className={`inline-flex items-center gap-2 rounded border border-slate-300 py-2 text-sm hover:bg-slate-100 ${mini ? "justify-center px-2" : "px-3"}`}
          >
            <LogOut size={14} /> {!mini && "Iesire"}
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div data-testid="layout-root" className="min-h-screen bg-slate-100 text-slate-900">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded bg-accent-500 text-ink-950 grid place-items-center font-bold text-sm">A</div>
          <h1 data-testid="layout-title-mobile" className="text-base font-semibold">{title || "Dashboard"}</h1>
        </div>
        <button
          data-testid="sidebar-toggle-btn"
          type="button"
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center justify-center rounded border border-slate-300 p-2 hover:bg-slate-100"
          aria-label="Deschide meniu"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* Desktop fixed sidebar */}
      <aside
        data-testid="sidebar-desktop"
        className={`hidden md:flex fixed inset-y-0 left-0 flex-col border-r border-slate-200 bg-white transition-[width] duration-200 ${collapsed ? "w-16" : "w-64"}`}
      >
        <SidebarContent mini={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-overlay"
          />
          <aside
            data-testid="sidebar-mobile"
            className="absolute inset-y-0 left-0 w-72 bg-white border-r border-slate-200 shadow-xl"
          >
            <button
              data-testid="sidebar-close-btn"
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 inline-flex items-center justify-center rounded p-1.5 hover:bg-slate-100"
              aria-label="Inchide meniu"
            >
              <X size={18} />
            </button>
            <SidebarContent />
          </aside>
        </div>
      ) : null}

      {/* Main content */}
      <div className={`flex min-h-screen flex-col transition-[padding] duration-200 ${collapsed ? "md:pl-16" : "md:pl-64"}`}>
        <header className="hidden md:block border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3 px-8 py-4">
            <button
              data-testid="sidebar-collapse-btn"
              type="button"
              onClick={toggleCollapsed}
              title={collapsed ? "Afiseaza meniul" : "Ascunde meniul"}
              aria-label={collapsed ? "Afiseaza meniul" : "Ascunde meniul"}
              className="inline-flex items-center justify-center rounded border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-100"
            >
              {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
            <h1 data-testid="layout-title" className="text-lg font-semibold">{title || "Dashboard"}</h1>
          </div>
        </header>
        <main className="w-full flex-1 px-4 py-6 md:px-8 md:py-8">{children}</main>
        <footer className="border-t border-slate-200 px-6 py-3 text-center text-xs text-slate-400">
          AmeMan {new Date().getFullYear()} - Amel International Group
        </footer>
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
